import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Modal } from '../../../components/common/Modal';
import apiClient from '../../../services/api';
import { ModelPricing, PaginatedResponse } from '../../../types';

interface Model {
  id: string;
  provider: string;
}

interface ModelsListResponse {
  object: string;
  data: Model[];
}

export function ModelsPage() {
  const [models, setModels] = useState<ModelPricing[]>([]);
  const [availableModels, setAvailableModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState<ModelPricing | null>(null);
  const [providerFilter, setProviderFilter] = useState('');
  const [providers, setProviders] = useState<string[]>([]);
  const pageSize = 20;

  const [formData, setFormData] = useState({
    model_id: '',
    model_name: '',
    provider: 'openai',
    input_rate: 0,
    input_rate_cache_hit: 0,
    output_rate: 0,
    currency: 'USD',
    status: 'active',
  });

  useEffect(() => {
    fetchModels();
    fetchProviders();
    fetchAvailableModels();
  }, [page, providerFilter]);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });
      if (providerFilter) {
        params.append('provider', providerFilter);
      }
      const response = await apiClient.get<PaginatedResponse<ModelPricing>>(
        `/admin/models?${params}`
      );
      setModels(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch models:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await apiClient.get<{ providers: string[] }>('/admin/models/providers');
      setProviders(response.data.providers || []);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    }
  };

  const fetchAvailableModels = async () => {
    try {
      const response = await apiClient.get<ModelsListResponse>('/models');
      setAvailableModels(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch available models:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingModel) {
        await apiClient.put(`/admin/models/${editingModel.id}`, formData);
      } else {
        await apiClient.post('/admin/models', formData);
      }
      setShowModal(false);
      setEditingModel(null);
      resetForm();
      fetchModels();
    } catch (error) {
      console.error('Failed to save model:', error);
    }
  };

  const handleEdit = (model: ModelPricing) => {
    setEditingModel(model);
    setFormData({
      model_id: model.model_id,
      model_name: model.model_name,
      provider: model.provider,
      input_rate: model.input_rate,
      input_rate_cache_hit: model.input_rate_cache_hit,
      output_rate: model.output_rate,
      currency: model.currency,
      status: model.status,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除这个模型定价吗？')) return;
    try {
      await apiClient.delete(`/admin/models/${id}`);
      fetchModels();
    } catch (error) {
      console.error('Failed to delete model:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      model_id: '',
      model_name: '',
      provider: 'openai',
      input_rate: 0,
      input_rate_cache_hit: 0,
      output_rate: 0,
      currency: 'USD',
      status: 'active',
    });
  };

  const formatRate = (rate: number) => {
    // 保留最多2位小数，去掉末尾的0
    const formattedRate = parseFloat(rate.toFixed(2));
    if (isNaN(formattedRate)) {
      return '$0/1M token';
    }
    return `$${formattedRate}/1M token`;
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">模型定价管理</h1>
        <Button
          onClick={() => {
            resetForm();
            setEditingModel(null);
            setShowModal(true);
          }}
        >
          添加模型
        </Button>
      </div>

      <Card>
        <div className="mb-4">
          <select
            value={providerFilter}
            onChange={(e) => {
              setProviderFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="">所有提供商</option>
            {providers.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : models.length === 0 ? (
          <p className="text-center text-gray-500 py-8">暂无模型定价</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">模型ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">模型名称</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">提供商</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">输入价格(未命中)</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">输入价格(命中)</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">输出价格</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">状态</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((model) => (
                    <tr key={model.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm font-mono">{model.model_id}</td>
                      <td className="py-3 px-4 text-sm">{model.model_name}</td>
                      <td className="py-3 px-4 text-sm">{model.provider}</td>
                      <td className="py-3 px-4 text-sm text-right">{formatRate(model.input_rate)}</td>
                      <td className="py-3 px-4 text-sm text-right">{formatRate(model.input_rate_cache_hit)}</td>
                      <td className="py-3 px-4 text-sm text-right">{formatRate(model.output_rate)}</td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            model.status === 'active'
                              ? 'text-green-600 bg-green-100'
                              : 'text-gray-600 bg-gray-100'
                          }`}
                        >
                          {model.status === 'active' ? '启用' : '禁用'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(model)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            编辑
                          </button>
                          <button
                            onClick={() => handleDelete(model.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">共 {total} 条记录</div>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    上一页
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingModel(null);
          resetForm();
        }}
        title={editingModel ? '编辑模型定价' : '添加模型定价'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">模型</label>
            <select
              value={formData.model_id}
              onChange={(e) => {
                const selectedModel = availableModels.find(m => m.id === e.target.value);
                setFormData({
                  ...formData,
                  model_id: e.target.value,
                  model_name: selectedModel ? e.target.value : '',
                  provider: selectedModel ? selectedModel.provider : 'openai',
                });
              }}
              className="w-full px-3 py-2 border rounded-lg"
              required
              disabled={!!editingModel}
            >
              <option value="">请选择模型</option>
              {availableModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.id} ({model.provider})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">模型名称</label>
            <input
              type="text"
              value={formData.model_name}
              onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="GPT-4"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">提供商</label>
            <select
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="openai">OpenAI</option>
              <option value="claude">Anthropic</option>
              <option value="google">Google</option>
              <option value="deepseek">DeepSeek</option>
              <option value="tongyi">通义千问</option>
              <option value="wenxin">文心一言</option>
              <option value="custom">自定义</option>
            </select>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                输入价格(未命中) (价格/1M token)
              </label>
              <input
                type="number"
                step="0.000001"
                value={formData.input_rate}
                onChange={(e) =>
                  setFormData({ ...formData, input_rate: parseFloat(e.target.value) || 0 })
                }
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                输入价格(命中) (价格/1M token)
              </label>
              <input
                type="number"
                step="0.000001"
                value={formData.input_rate_cache_hit}
                onChange={(e) =>
                  setFormData({ ...formData, input_rate_cache_hit: parseFloat(e.target.value) || 0 })
                }
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                输出价格 (价格/1M token)
              </label>
              <input
                type="number"
                step="0.000001"
                value={formData.output_rate}
                onChange={(e) =>
                  setFormData({ ...formData, output_rate: parseFloat(e.target.value) || 0 })
                }
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">货币</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="USD">USD</option>
                <option value="CNY">CNY</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">状态</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="active">启用</option>
                <option value="inactive">禁用</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setEditingModel(null);
                resetForm();
              }}
            >
              取消
            </Button>
            <Button type="submit">{editingModel ? '更新' : '创建'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
